import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Building2, User, Home, FileSignature, FileUp, Trash2 } from "lucide-react";
import Layout from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const DRAFT_KEY = "propiedad_wizard_draft";

const ESTADOS = [
  { value: "disponible", label: "Disponible", desc: "Lista para rentarse", color: "border-blue-300 bg-blue-50" },
  { value: "rentada", label: "Rentada", desc: "Con inquilino y contrato", color: "border-emerald-300 bg-emerald-50" },
  { value: "uso_propietario", label: "Uso del propietario", desc: "El dueño la ocupa", color: "border-gray-300 bg-gray-50" },
  { value: "en_mantenimiento", label: "En mantenimiento", desc: "En reparación", color: "border-orange-300 bg-orange-50" },
  { value: "inactiva", label: "Inactiva", desc: "Fuera de operación", color: "border-gray-200 bg-gray-50" },
];

const initialForm = {
  nombre_corto: "", direccion: "", tipo: "Departamento", superficie: "", notas: "",
  cuota_condominal: "", cuota_condominal_paga: "inquilino",
  propietario_nombre: "", propietario_telefono: "", propietario_email: "", comision_porcentaje: "",
  estado: "disponible",
  inquilino_nombre: "", inquilino_telefono: "", inquilino_email: "",
  renta_mensual: "", deposito_garantia: "", fecha_inicio: "", fecha_termino: "", dia_pago: "1",
};

export default function PropiedadWizard({ user, onLogout }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    try {
      const draft = sessionStorage.getItem(DRAFT_KEY);
      return draft ? { ...initialForm, ...JSON.parse(draft) } : initialForm;
    } catch { return initialForm; }
  });
  const [documentos, setDocumentos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form));
  }, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const esRentada = form.estado === "rentada";
  const pasos = esRentada
    ? [{ n: 1, label: "Inmueble", icon: Building2 }, { n: 2, label: "Propietario", icon: User }, { n: 3, label: "Estado", icon: Home }, { n: 4, label: "Contrato", icon: FileSignature }, { n: 5, label: "Documentos", icon: FileUp }]
    : [{ n: 1, label: "Inmueble", icon: Building2 }, { n: 2, label: "Propietario", icon: User }, { n: 3, label: "Estado", icon: Home }, { n: 5, label: "Documentos", icon: FileUp }];

  const validarPaso = (s) => {
    if (s === 1) {
      if (!form.nombre_corto.trim() || !form.direccion.trim()) { toast.error("Nombre corto y dirección son requeridos"); return false; }
    }
    if (s === 2 && esRentada) {
      if (!form.propietario_nombre.trim()) { toast.error("El nombre del propietario es requerido"); return false; }
    }
    if (s === 4) {
      if (!form.inquilino_nombre || !form.inquilino_telefono || !form.inquilino_email || !form.renta_mensual || !form.fecha_inicio || !form.fecha_termino) {
        toast.error("Completa inquilino, renta y fechas del contrato"); return false;
      }
      if (!form.propietario_nombre || !form.propietario_telefono || !form.propietario_email) {
        toast.error("Para crear el contrato se requiere nombre, teléfono y email del propietario (Paso 2)"); return false;
      }
      if (form.fecha_termino <= form.fecha_inicio) { toast.error("La fecha de término debe ser posterior al inicio"); return false; }
    }
    return true;
  };

  const siguiente = () => {
    if (!validarPaso(step)) return;
    if (step === 3) setStep(esRentada ? 4 : 5);
    else setStep(step + 1);
  };
  const anterior = () => {
    if (step === 5) setStep(esRentada ? 4 : 3);
    else setStep(step - 1);
  };

  const handleFinalizar = async () => {
    if (!validarPaso(1) || (esRentada && !validarPaso(4))) return;
    setSaving(true);
    try {
      // 1. Crear propiedad
      const propRes = await axios.post(`${API}/propiedades`, {
        nombre_corto: form.nombre_corto,
        direccion: form.direccion,
        tipo: form.tipo,
        superficie: form.superficie ? parseFloat(form.superficie) : null,
        propietario_nombre: form.propietario_nombre || null,
        propietario_telefono: form.propietario_telefono || null,
        propietario_email: form.propietario_email || null,
        comision_porcentaje: form.comision_porcentaje !== "" ? parseFloat(form.comision_porcentaje) : null,
        estado_manual: ["uso_propietario", "en_mantenimiento", "inactiva"].includes(form.estado) ? form.estado : null,
        notas: form.notas || null,
      }, { headers: authHeader() });
      const propId = propRes.data.id;

      // 2. Contrato si está rentada
      if (esRentada) {
        try {
          await axios.post(`${API}/contracts`, {
            propietario_nombre: form.propietario_nombre,
            propietario_telefono: form.propietario_telefono,
            propietario_email: form.propietario_email,
            inquilino_nombre: form.inquilino_nombre,
            inquilino_telefono: form.inquilino_telefono,
            inquilino_email: form.inquilino_email,
            inmueble_nombre: form.nombre_corto,
            inmueble_direccion: form.direccion,
            inmueble_tipo: form.tipo,
            inmueble_superficie: form.superficie ? parseFloat(form.superficie) : null,
            renta_mensual: parseFloat(form.renta_mensual),
            fecha_inicio: form.fecha_inicio,
            fecha_termino: form.fecha_termino,
            deposito_garantia: form.deposito_garantia ? parseFloat(form.deposito_garantia) : 0,
            dia_pago: parseInt(form.dia_pago) || 1,
            comision_porcentaje: form.comision_porcentaje !== "" ? parseFloat(form.comision_porcentaje) : null,
          }, { headers: authHeader() });
        } catch (e) {
          toast.error("Propiedad creada, pero falló el contrato: " + (e.response?.data?.detail || "error"));
        }
      }

      // 3. Documentos iniciales
      let docsOk = 0;
      for (const d of documentos) {
        if (!d.file) continue;
        try {
          const fd = new FormData();
          fd.append("titulo", d.titulo || d.file.name);
          fd.append("categoria", d.categoria || "Propiedad");
          fd.append("file", d.file);
          await axios.post(`${API}/propiedades/${propId}/documentos`, fd, { headers: authHeader() });
          docsOk++;
        } catch { /* continúa con los demás */ }
      }

      sessionStorage.removeItem(DRAFT_KEY);
      toast.success(`Propiedad creada${esRentada ? " con contrato y calendario de rentas" : ""}${docsOk ? ` · ${docsOk} documentos subidos` : ""}`);
      navigate(`/propiedades/${propId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear la propiedad");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="propiedad-wizard">
        <button onClick={() => navigate("/propiedades")} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Volver a propiedades
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Alta de propiedad</h1>
          <p className="text-sm text-gray-500 mt-1">Tu avance se guarda automáticamente como borrador</p>
        </div>

        {/* Progreso */}
        <div className="flex items-center gap-1">
          {pasos.map((p, i) => (
            <div key={p.n} className="flex items-center flex-1 last:flex-none">
              <button
                onClick={() => p.n < step && setStep(p.n)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  step === p.n ? "bg-gray-900 text-white" : p.n < step ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"
                }`}
                data-testid={`wizard-paso-${p.n}`}
              >
                {p.n < step ? <Check className="w-3.5 h-3.5" /> : <p.icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{p.label}</span>
              </button>
              {i < pasos.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${p.n < step ? "bg-emerald-300" : "bg-gray-200"}`} />}
            </div>
          ))}
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            {step === 1 && (
              <>
                <h2 className="font-semibold text-gray-900">Paso 1 · Datos del inmueble</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre corto *</Label>
                    <Input value={form.nombre_corto} onChange={(e) => set("nombre_corto", e.target.value)} placeholder="Ej: Kartesia 701" data-testid="wizard-nombre-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
                      <SelectTrigger data-testid="wizard-tipo-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Departamento", "Casa", "Local", "Oficina", "Bodega", "Otro"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Dirección *</Label>
                  <Input value={form.direccion} onChange={(e) => set("direccion", e.target.value)} placeholder="Calle, número, colonia, ciudad" data-testid="wizard-direccion-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Superficie (m²)</Label>
                    <Input type="number" step="0.01" value={form.superficie} onChange={(e) => set("superficie", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Cuota condominal ($/mes)</Label>
                    <Input type="number" step="0.01" min="0" value={form.cuota_condominal} onChange={(e) => set("cuota_condominal", e.target.value)} data-testid="wizard-cuota-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>¿Quién paga la cuota?</Label>
                    <Select value={form.cuota_condominal_paga} onValueChange={(v) => set("cuota_condominal_paga", v)}>
                      <SelectTrigger data-testid="wizard-cuota-paga-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inquilino">El inquilino</SelectItem>
                        <SelectItem value="propietario">El propietario directo</SelectItem>
                        <SelectItem value="administracion">La administración (se descuenta)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Textarea rows={2} value={form.notas} onChange={(e) => set("notas", e.target.value)} placeholder="Información condominal, servicios, datos fiscales..." />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <h2 className="font-semibold text-gray-900">Paso 2 · Propietario y comisión</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre del propietario {esRentada && "*"}</Label>
                    <Input value={form.propietario_nombre} onChange={(e) => set("propietario_nombre", e.target.value)} data-testid="wizard-propietario-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input value={form.propietario_telefono} onChange={(e) => set("propietario_telefono", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={form.propietario_email} onChange={(e) => set("propietario_email", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Comisión de administración (%)</Label>
                    <Input type="number" step="0.01" min="0" max="100" value={form.comision_porcentaje} onChange={(e) => set("comision_porcentaje", e.target.value)} placeholder="Ej: 8" data-testid="wizard-comision-input" />
                    <p className="text-xs text-gray-500">Se usará para calcular la comisión automática al cobrar rentas</p>
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <h2 className="font-semibold text-gray-900">Paso 3 · Estado de la propiedad</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ESTADOS.map((e) => (
                    <button
                      key={e.value}
                      onClick={() => set("estado", e.value)}
                      className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${form.estado === e.value ? e.color + " ring-2 ring-gray-900/10" : "border-gray-100 hover:border-gray-200"}`}
                      data-testid={`wizard-estado-${e.value}`}
                    >
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        {form.estado === e.value && <Check className="w-4 h-4" />} {e.label}
                      </p>
                      <p className="text-xs text-gray-500">{e.desc}</p>
                    </button>
                  ))}
                </div>
                {esRentada && <p className="text-xs text-indigo-600 bg-indigo-50 rounded-lg px-3 py-2">En el siguiente paso capturarás el contrato. Se generará automáticamente el calendario de rentas y las alertas de renovación.</p>}
              </>
            )}

            {step === 4 && (
              <>
                <h2 className="font-semibold text-gray-900">Paso 4 · Contrato de arrendamiento</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Inquilino *</Label>
                    <Input value={form.inquilino_nombre} onChange={(e) => set("inquilino_nombre", e.target.value)} data-testid="wizard-inquilino-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono del inquilino *</Label>
                    <Input value={form.inquilino_telefono} onChange={(e) => set("inquilino_telefono", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email del inquilino *</Label>
                    <Input type="email" value={form.inquilino_email} onChange={(e) => set("inquilino_email", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Renta mensual *</Label>
                    <Input type="number" step="0.01" value={form.renta_mensual} onChange={(e) => set("renta_mensual", e.target.value)} data-testid="wizard-renta-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de inicio *</Label>
                    <Input type="date" value={form.fecha_inicio} onChange={(e) => set("fecha_inicio", e.target.value)} data-testid="wizard-inicio-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de término *</Label>
                    <Input type="date" value={form.fecha_termino} onChange={(e) => set("fecha_termino", e.target.value)} data-testid="wizard-termino-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Depósito en garantía</Label>
                    <Input type="number" step="0.01" value={form.deposito_garantia} onChange={(e) => set("deposito_garantia", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Día de pago</Label>
                    <Input type="number" min="1" max="31" value={form.dia_pago} onChange={(e) => set("dia_pago", e.target.value)} />
                  </div>
                </div>
                <p className="text-xs text-gray-500">¿Necesitas fiadores, incremento anual u opciones avanzadas? Podrás editarlas después desde el contrato.</p>
              </>
            )}

            {step === 5 && (
              <>
                <h2 className="font-semibold text-gray-900">Paso 5 · Documentos iniciales (opcional)</h2>
                {documentos.map((d, i) => (
                  <div key={i} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center border border-gray-100 rounded-lg p-3">
                    <Input
                      placeholder="Título (ej: Escrituras, INE propietario)"
                      value={d.titulo}
                      onChange={(e) => setDocumentos(documentos.map((x, j) => j === i ? { ...x, titulo: e.target.value } : x))}
                      className="sm:w-56"
                    />
                    <Input
                      type="file"
                      onChange={(e) => setDocumentos(documentos.map((x, j) => j === i ? { ...x, file: e.target.files?.[0] || null } : x))}
                      className="flex-1"
                    />
                    <Button size="sm" variant="ghost" onClick={() => setDocumentos(documentos.filter((_, j) => j !== i))}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setDocumentos([...documentos, { titulo: "", file: null }])} data-testid="wizard-agregar-doc-btn">
                  <FileUp className="w-4 h-4 mr-1" /> Agregar documento
                </Button>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm space-y-1">
                  <p className="font-medium text-gray-900">Resumen</p>
                  <p className="text-gray-600">{form.nombre_corto} · {form.tipo} · {ESTADOS.find((e) => e.value === form.estado)?.label}</p>
                  {form.propietario_nombre && <p className="text-gray-600">Propietario: {form.propietario_nombre}{form.comision_porcentaje !== "" ? ` · Comisión ${form.comision_porcentaje}%` : ""}</p>}
                  {esRentada && form.inquilino_nombre && <p className="text-gray-600">Contrato: {form.inquilino_nombre} · ${form.renta_mensual}/mes · {form.fecha_inicio} → {form.fecha_termino}</p>}
                  {documentos.filter((d) => d.file).length > 0 && <p className="text-gray-600">{documentos.filter((d) => d.file).length} documentos por subir</p>}
                </div>
              </>
            )}

            {/* Navegación */}
            <div className="flex justify-between pt-4 border-t border-gray-100">
              <Button variant="outline" onClick={anterior} disabled={step === 1}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Anterior
              </Button>
              {step < 5 ? (
                <Button onClick={siguiente} data-testid="wizard-siguiente-btn">
                  Siguiente <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleFinalizar} disabled={saving} data-testid="wizard-finalizar-btn">
                  {saving ? "Creando..." : "Crear propiedad"} <Check className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
